/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import * as React from 'react';
import { useContext } from 'react';
import { Avatar, Box, Divider, Icon, IconButton, ListItemIcon, MenuItem, Theme, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../data/store.typings';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { LoginResponseData } from '../../data/api/auth/authClient.typings';
import Menu from '@mui/material/Menu';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { theme } from '../../assets/style/them';
import { authClient } from '../../data/api/auth/authClient';
import { logoutUser, saveLastMonitoringRouteIdAction } from '../../data/actions/userActions';
import { setNotificationAction } from '../../data/actions/notificationActions';
import { actions } from 'redux-router5';
import { RouteId } from 'router/routes';
import { TableTooltip } from '../StyledComponents/TableTooltip';
import { selectCurrentRouteName, selectCurrentRouteParams } from '../../data/store-selectors';
import { getFullUserName, parseError } from '../../utils/utils';
import { AppAbilityContext, Can } from '../../accessibility/ability';
import PersonIcon from '@mui/icons-material/Person';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        avatarWrap: {
            display: 'flex',
            alignItems: 'center',
        },
        avatar: {
            background: theme.palette.background.paper,
            color: theme.palette.primary.main,
            boxShadow: 'none',
            '& svg': {
                fontSize: 26,
            },
            '&:hover': {
                background: theme.palette.background.paper,
            },
        },
        menu: {
            padding: 0,
        },
        menuHeader: {
            margin: theme.spacing(0.4, 2, 1, 2),
            display: 'flex',
            flexDirection: 'column',
        },
        icon: {
            color: theme.palette.text.primary,
            minWidth: '40px',
        },
    }),
);

interface AccountMenuProps {
    isMatchesXS: boolean;
}

export const AccountMenu = (props: AccountMenuProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const authData: LoginResponseData = useSelector(selectAuthData, shallowEqual);
    const routeName = useSelector(selectCurrentRouteName, shallowEqual);
    const routeParams = useSelector(selectCurrentRouteParams, shallowEqual);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const dispatch = useDispatch();
    const ability = useContext(AppAbilityContext);

    const fullUserName = getFullUserName(authData.firstName, authData.lastName);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const saveLastMonitoringRouteId = () => {
        if (!routeName.includes(RouteId.System)) {
            dispatch(saveLastMonitoringRouteIdAction({ routeId: routeName, params: routeParams }));
        }
    };

    const menuItemActionConfig = {
        LOGOUT: () =>
            authClient
                .logout()
                .then(() => {
                    dispatch(logoutUser());
                })
                .catch((error) => dispatch(setNotificationAction(parseError(error)))),
        SETTINGS: () => {
            saveLastMonitoringRouteId();
            if (ability.can('viewScreen', 'USER_SETTINGS_INFO_SCREEN')) {
                dispatch(actions.navigateTo(RouteId.SystemSettingsInfo));
            } else if (ability.can('viewScreen', 'USER_SETTINGS_SECURITY_SCREEN')) {
                dispatch(actions.navigateTo(RouteId.SystemSettingsSecurity));
            }
        },
        ADMIN_PANEL: () => {
            saveLastMonitoringRouteId();
            //permit is checked before
            dispatch(actions.navigateTo(RouteId.SystemAdmin));
        },
    };

    return authData ? (
        <React.Fragment>
            <div className={classes.avatarWrap}>
                <Box pr={1.5} pl={1.5}>
                    <TableTooltip title={t('domain.accountMenu')} placement="left">
                        <IconButton aria-label="Account Menu" onClick={handleClick} size="small">
                            <Avatar className={classes.avatar}>
                                <PersonIcon />
                            </Avatar>
                        </IconButton>
                    </TableTooltip>
                </Box>
                {!props.isMatchesXS ? (
                    <Typography variant="subtitle1" color={'textPrimary'}>
                        {fullUserName}
                    </Typography>
                ) : null}
            </div>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    style: {
                        minWidth: 280,
                    },
                }}
            >
                <div className={classes.menuHeader}>
                    <Typography variant="subtitle1" color={'textPrimary'}>
                        {fullUserName}
                    </Typography>

                    <Typography variant="subtitle2" style={{ color: theme.palette.disabledText.main }}>
                        {authData.email ? authData.email : authData.phone}
                    </Typography>
                </div>

                <Divider />

                {(ability.can('viewScreen', 'USER_SETTINGS_INFO_SCREEN') ||
                    ability.can('viewScreen', 'USER_SETTINGS_SECURITY_SCREEN')) && (
                    <Box mt={1} key={'SETTINGS'}>
                        <MenuItem onClick={menuItemActionConfig['SETTINGS']}>
                            <ListItemIcon className={classes.icon}>
                                <Icon>{'settings'}</Icon>
                            </ListItemIcon>
                            {t('SystemScreens.userProfile')}
                        </MenuItem>
                    </Box>
                )}

                <Can do={'viewScreen'} on={'ADMIN_USERS_LIST_SCREEN'}>
                    <Box mt={1} key={'ADMIN_PANEL'}>
                        <MenuItem onClick={menuItemActionConfig['ADMIN_PANEL']}>
                            <ListItemIcon className={classes.icon}>
                                <Icon>{'supervised_user_circle'}</Icon>
                            </ListItemIcon>
                            {t('SystemScreens.adminPanel')}
                        </MenuItem>
                    </Box>
                </Can>

                <Box mt={1} key={'LOGOUT'}>
                    <MenuItem onClick={menuItemActionConfig['LOGOUT']}>
                        <ListItemIcon className={classes.icon}>
                            <Icon>{'logout'}</Icon>
                        </ListItemIcon>
                        {t('AuthScreen.logout')}
                    </MenuItem>
                </Box>
            </Menu>
        </React.Fragment>
    ) : null;
};

const selectAuthData = (state: RootState): LoginResponseData => {
    return state.userReducer.authData;
};
